import { forIn, isEmpty } from 'lodash';
import { getStore } from '@/store/zeroDepStore';
const AI_PROJECT_INFO_BASE_EXCLUDE_FIELDS = ['ctime', 'mtime', '_ctime', '_mtime'];
export const createAiProjectInfoExclude = ({ excludeArtifact = false } = {}) => [
    ...(excludeArtifact ? ['artifact'] : []),
    ...AI_PROJECT_INFO_BASE_EXCLUDE_FIELDS,
].join(',');
const isAiDeployCosEnabled = (aiDeploySetting) => {
    if (!aiDeploySetting)
        return false;
    if (aiDeploySetting.cosEnable !== true)
        return false;
    return !!aiDeploySetting.cosId;
};
const getProjectAiDeploySetting = () => {
    try {
        return getStore()?.state?.project?.settings?.aiDeploySetting || null;
    }
    catch {
        return null;
    }
};
export const getAiArtifactCosEnabled = () => {
    if (window.GLOBAL_INFO?.configuredAiProjectUploader)
        return true;
    return isAiDeployCosEnabled(getProjectAiDeploySetting());
};
/** crossorigin === false 时不注入 preload（HTML 与下方动态 link 一致），否则使用 anonymous */
const shouldInjectAiArtifactPreload = () => window.GLOBAL_INFO?.aiProjectArtifactConfig?.crossorigin !== false;
// 是否启用 slim 模式（仅请求 artifactCos 元信息，artifact 由 COS 还原）。
// 配置了 aiProjectUploader 即开启，dev/branch 也走 slim：服务端已统一在 dev/branch 上传 artifactCos。
// 入参 env/branchId 仅为兼容历史调用方保留，新逻辑不再依赖。
export const shouldUseSlimAiProjectInfo = (_options = {}) => getAiArtifactCosEnabled();
// JS 是进入 COS 路径的最小必要条件，CSS 允许为空字符串或缺省。
export const hasCompleteArtifactCos = (aiProjectInfo) => !!aiProjectInfo?.artifactCos?.js;
const hasExistingPreload = (href, as) => Array
    .from(document.head.querySelectorAll(`link[rel="preload"][as="${as}"]`))
    .some((node) => {
    const link = node;
    return link.href === href || link.getAttribute('href') === href;
});
const appendPreload = (href, as) => {
    if (typeof document === 'undefined' || !href || hasExistingPreload(href, as))
        return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = as;
    link.href = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
};
export const preloadArtifactCosAssets = (artifactCos) => {
    if (!artifactCos || !shouldInjectAiArtifactPreload())
        return;
    if (artifactCos.css)
        appendPreload(artifactCos.css, 'style');
    if (artifactCos.js)
        appendPreload(artifactCos.js, 'script');
    if (!isEmpty(artifactCos.assetFiles)) {
        forIn(artifactCos.assetFiles, (asset) => {
            if (asset.url)
                appendPreload(asset.url, 'script');
        });
    }
};
const ARTIFACT_COS_STYLESHEET_ATTR = 'data-wuji-gogo-artifact-cos-css';
function isCssAssetPath(path) {
    return /\.css($|\?)/i.test(path.split('?')[0]);
}
function hasExistingStylesheet(href) {
    return Array
        .from(document.head.querySelectorAll('link[rel="stylesheet"]'))
        .some((node) => {
        const link = node;
        return link.href === href || link.getAttribute('href') === href;
    });
}
function appendStylesheet(href) {
    if (typeof document === 'undefined' || !href || hasExistingStylesheet(href)) {
        return Promise.resolve(null);
    }
    return new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = href;
        link.setAttribute(ARTIFACT_COS_STYLESHEET_ATTR, '1');
        if (shouldInjectAiArtifactPreload()) {
            link.crossOrigin = 'anonymous';
        }
        link.onload = () => resolve(link);
        link.onerror = () => reject(new Error(`Failed to load stylesheet: ${href}`));
        document.head.appendChild(link);
    });
}
export function collectArtifactCosStylesheetUrls(artifactCos) {
    if (!artifactCos)
        return [];
    const urls = [];
    if (artifactCos.css) {
        urls.push(artifactCos.css);
    }
    // 页面 chunk 抽出的 scoped css（esbuild 不会随 JS import 自动挂）
    if (!isEmpty(artifactCos.assetFiles)) {
        forIn(artifactCos.assetFiles, (asset, path) => {
            if (asset.url && isCssAssetPath(path)) {
                urls.push(asset.url);
            }
        });
    }
    return [...new Set(urls)];
}
async function loadStylesheetUrls(urls) {
    const results = await Promise.allSettled(urls.map(url => appendStylesheet(url)));
    results.forEach((result, index) => {
        if (result.status === 'rejected') {
            console.error('[xy-client] load artifactCos stylesheet failed', urls[index], result.reason);
        }
    });
}
/** 只等入口 css；chunk css 后台挂，不挡首屏模块加载 */
export async function loadArtifactCosStylesheets(artifactCos) {
    if (!artifactCos)
        return;
    if (artifactCos.css) {
        await loadStylesheetUrls([artifactCos.css]);
    }
    const chunkCss = collectArtifactCosStylesheetUrls(artifactCos).filter(url => url !== artifactCos.css);
    if (chunkCss.length) {
        void loadStylesheetUrls(chunkCss);
    }
}
export function disposeArtifactCosStylesheets() {
    if (typeof document === 'undefined')
        return;
    document.head.querySelectorAll(`link[${ARTIFACT_COS_STYLESHEET_ATTR}]`).forEach(el => el.remove());
}
const fetchAssetText = async (url) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch AI artifact asset: ${url}`);
    }
    return await response.text();
};
export const resolveArtifactCosToArtifact = async (artifactCos) => {
    if (!artifactCos?.js) {
        throw new Error('artifactCos.js is required');
    }
    preloadArtifactCosAssets(artifactCos);
    const [js, css] = await Promise.all([
        fetchAssetText(artifactCos.js),
        artifactCos.css ? fetchAssetText(artifactCos.css) : Promise.resolve(artifactCos?.css ?? ''),
    ]);
    return {
        ...artifactCos,
        js,
        css,
    };
};
